import { IconButton, Tooltip } from '@sweep-io/sweep-design';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { Settings } from '@sweep-io/sweep-design/dist/icons';
import usePopover from '../../../hooks/usePopover';
import { MenuItem, Popover, Stack } from '@mui/material';
import { useState } from 'react';
import { InstructionsDialog } from './instructions/InstructionsDialog';
import { usePermissions } from '../../../permissions/usePermissions';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';

interface SettingsWrapperProps {
  setAiAssistantDialogOpen: (open: boolean) => void;
  isCollapsed?: boolean;
}

interface SettingsMenuItem {
  label: string;
  onClick: () => void;
  restrictedText?: string;
  allowed?: boolean;
  isDisabled?: boolean;
}

export const SettingsWrapper = ({
  setAiAssistantDialogOpen,
  isCollapsed,
}: SettingsWrapperProps) => {
  const { supportAgentInAgenticCenter } = useFeatureToggle();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId) ?? '';
  const [isInstructionsDialogOpen, setIsInstructionsDialogOpen] = useState(false);

  const [isInstructionsAllowed] = usePermissions().evaluatePermissions([{ permission: ['create:ai-sessions'], crmOrgId }]);
  const { openPopover, closePopover, isPopoverOpen, anchorElement, anchorRef } = usePopover();

  const menuItems: SettingsMenuItem[] = [
    {
      label: 'Salesforce apps',
      onClick: () => {
        setAiAssistantDialogOpen(true);
      },
    },
    {
      label: 'Instructions',
      onClick: () => setIsInstructionsDialogOpen(true),
      allowed: isInstructionsAllowed,
      restrictedText: 'To see instructions, please contact your admin',
      isDisabled: !isInstructionsAllowed,
    },
  ];

  const renderMenuItem = (item: SettingsMenuItem, isDisabled?: boolean) => (
    <MenuItem
      key={item.label}
      disabled={isDisabled}
      onClick={() => {
        item.onClick();
        closePopover();
      }}
    >
      {item.label}
    </MenuItem>
  );

  return (
    <>
      {isInstructionsDialogOpen && (
        <InstructionsDialog onClose={() => setIsInstructionsDialogOpen(false)} />
      )}

      <Tooltip title="Settings">
        <IconButton
          onClick={() => {
            if (supportAgentInAgenticCenter) {
              setAiAssistantDialogOpen(true);
            } else {
              openPopover();
            }
          }}
          size={isCollapsed ? 'tiny' : 'small'}
          variant="system"
          ref={anchorRef}
        >
          <Settings />
        </IconButton>
      </Tooltip>

      <Popover
        open={isPopoverOpen}
        onClose={closePopover}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Stack p={1} sx={{ width: '276px' }}>
          {menuItems.map((item) => {
            if (item.allowed) {
              return (
                <RestrictedTooltip
                  notAllowedTitle={item.restrictedText}
                  validation={{ type: 'explicit', allowed: item.allowed }}
                  key={item.label}
                >
                  {renderMenuItem(item, item.isDisabled)}
                </RestrictedTooltip>
              );
            }
            return renderMenuItem(item, item.isDisabled);
          })}
        </Stack>
      </Popover>
    </>
  );
};
